/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Journal Explorer Page - SuperAdmin
 * ============================================================================
 *
 * Cross-organization journal entry explorer for SuperAdmin auditing.
 * Displays paginated journal entries with expandable line details,
 * source type filtering, and correlated entry lookup.
 *
 * ============================================================================
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  AlertTriangle,
  Link2,
  X,
  Search,
} from 'lucide-react';
import { useJournalEntries, useJournalEntry, useCorrelatedEntries } from '@/hooks/api/useJournalLedger';
import { useOrganizations } from '@/hooks/api/useOrganizations';
import type { JournalEntryDto, JournalLineDto } from '@/services/superadmin/journal-ledger.service';

// ── Constants ────────────────────────────────────────────────────────────────

const SOURCE_TYPES = [
  'DEPOSIT',
  'WITHDRAWAL',
  'TRANSFER',
  'INVOICE_PAYMENT',
  'CREDIT_NOTE',
  'DEBIT_NOTE',
  'ADJUSTMENT',
  'COMBINED_PAYMENT',
  'REVERSAL',
  'OPENING_BALANCE',
] as const;

const PAGE_SIZE = 20;

// ── Source Type Color Map ────────────────────────────────────────────────────

function getSourceTypeStyle(sourceType: string): { bg: string; color: string } {
  switch (sourceType) {
    case 'DEPOSIT':
      return { bg: 'var(--color-success-light)', color: 'var(--color-success)' };
    case 'WITHDRAWAL':
      return { bg: 'var(--color-error-light)', color: 'var(--color-error)' };
    case 'TRANSFER':
      return { bg: 'var(--color-info-light)', color: 'var(--color-info)' };
    case 'INVOICE_PAYMENT':
      return { bg: 'rgba(147, 51, 234, 0.12)', color: 'rgb(147, 51, 234)' };
    case 'CREDIT_NOTE':
      return { bg: 'rgba(236, 72, 153, 0.12)', color: 'rgb(236, 72, 153)' };
    case 'DEBIT_NOTE':
      return { bg: 'rgba(249, 115, 22, 0.12)', color: 'rgb(249, 115, 22)' };
    case 'ADJUSTMENT':
      return { bg: 'var(--color-warning-light)', color: 'var(--color-warning)' };
    case 'COMBINED_PAYMENT':
      return { bg: 'rgba(6, 182, 212, 0.12)', color: 'rgb(6, 182, 212)' };
    case 'REVERSAL':
      return { bg: 'rgba(239, 68, 68, 0.15)', color: 'rgb(239, 68, 68)' };
    case 'OPENING_BALANCE':
      return { bg: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' };
    default:
      return { bg: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' };
  }
}

function getStatusStyle(status: string): { bg: string; color: string } {
  switch (status) {
    case 'POSTED':
      return { bg: 'var(--color-success-light)', color: 'var(--color-success)' };
    case 'REVERSED':
      return { bg: 'var(--color-warning-light)', color: 'var(--color-warning)' };
    default:
      return { bg: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' };
  }
}

// ── Correlated Entries Modal ─────────────────────────────────────────────────

function CorrelatedEntriesModal({
  correlationId,
  orgId,
  onClose,
  t,
}: {
  correlationId: string;
  orgId: string;
  onClose: () => void;
  t: (key: string) => string;
}) {
  const { data: entries, isLoading, error } = useCorrelatedEntries(correlationId, orgId);

  return (
    <div className="je-modal-overlay" onClick={onClose}>
      <div className="je-modal" onClick={(e) => e.stopPropagation()}>
        <div className="je-modal-header">
          <div className="je-modal-title-row">
            <Link2 size={16} />
            <h3 className="je-modal-title">{t('platform:journal.explorer.correlatedEntries')}</h3>
          </div>
          <span className="je-modal-corr-id">{correlationId}</span>
          <button className="je-modal-close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className="je-modal-body">
          {isLoading && (
            <div className="je-center-state">
              <RefreshCw className="je-spin" size={20} />
              <span>{t('platform:journal.explorer.loading')}</span>
            </div>
          )}
          {error && (
            <div className="je-center-state je-err">
              <AlertTriangle size={20} />
              <span>{t('platform:journal.explorer.loadError')}</span>
            </div>
          )}
          {entries && entries.length === 0 && (
            <div className="je-center-state">
              <span>{t('platform:journal.explorer.noCorrelatedEntries')}</span>
            </div>
          )}
          {entries && entries.length > 0 && (
            <div className="je-tbl-wrap">
              <table className="je-tbl">
                <thead>
                  <tr>
                    <th>{t('platform:journal.explorer.columns.entryNumber')}</th>
                    <th>{t('platform:journal.explorer.columns.date')}</th>
                    <th>{t('platform:journal.explorer.columns.description')}</th>
                    <th>{t('platform:journal.explorer.columns.sourceType')}</th>
                    <th className="je-tbl-right">{t('platform:journal.explorer.columns.totalDebit')}</th>
                    <th className="je-tbl-right">{t('platform:journal.explorer.columns.totalCredit')}</th>
                    <th>{t('platform:journal.explorer.columns.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => {
                    const srcStyle = getSourceTypeStyle(entry.sourceType);
                    const statusStyle = getStatusStyle(entry.status);
                    return (
                      <tr key={entry.id}>
                        <td className="je-tbl-mono">{entry.entryNumber}</td>
                        <td className="je-tbl-muted">
                          {new Date(entry.entryDate).toLocaleDateString()}
                        </td>
                        <td>{entry.description}</td>
                        <td>
                          <span
                            className="je-badge"
                            style={{ background: srcStyle.bg, color: srcStyle.color }}
                          >
                            {entry.sourceType}
                          </span>
                        </td>
                        <td className="je-tbl-right je-tbl-mono">
                          {entry.totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="je-tbl-right je-tbl-mono">
                          {entry.totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td>
                          <span
                            className="je-badge"
                            style={{ background: statusStyle.bg, color: statusStyle.color }}
                          >
                            {entry.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Expanded Entry Detail ────────────────────────────────────────────────────

function ExpandedEntryDetail({
  entryId,
  orgId,
  onViewCorrelated,
  t,
}: {
  entryId: string;
  orgId: string;
  onViewCorrelated: (correlationId: string) => void;
  t: (key: string) => string;
}) {
  const { data: entry, isLoading, error } = useJournalEntry(entryId, orgId);

  if (isLoading) {
    return (
      <tr className="je-expanded-row">
        <td colSpan={7}>
          <div className="je-expanded-content">
            <div className="je-center-state je-center-state-sm">
              <RefreshCw className="je-spin" size={16} />
              <span>{t('platform:journal.explorer.loadingLines')}</span>
            </div>
          </div>
        </td>
      </tr>
    );
  }

  if (error || !entry) {
    return (
      <tr className="je-expanded-row">
        <td colSpan={7}>
          <div className="je-expanded-content">
            <div className="je-center-state je-center-state-sm je-err">
              <AlertTriangle size={16} />
              <span>{t('platform:journal.explorer.loadError')}</span>
            </div>
          </div>
        </td>
      </tr>
    );
  }

  const lines: JournalLineDto[] = entry.lines ?? [];

  return (
    <tr className="je-expanded-row">
      <td colSpan={7}>
        <div className="je-expanded-content">
          {/* Meta info */}
          <div className="je-expanded-meta">
            <div className="je-expanded-meta-item">
              <span className="je-expanded-meta-label">{t('platform:journal.explorer.detail.entryNumber')}</span>
              <span className="je-expanded-meta-value">{entry.entryNumber}</span>
            </div>
            <div className="je-expanded-meta-item">
              <span className="je-expanded-meta-label">{t('platform:journal.explorer.detail.sourceId')}</span>
              <span className="je-expanded-meta-value je-tbl-mono">{entry.sourceId || '-'}</span>
            </div>
            <div className="je-expanded-meta-item">
              <span className="je-expanded-meta-label">{t('platform:journal.explorer.detail.createdAt')}</span>
              <span className="je-expanded-meta-value">
                {new Date(entry.createdAt).toLocaleString()}
              </span>
            </div>
            {entry.reversedById && (
              <div className="je-expanded-meta-item">
                <span className="je-expanded-meta-label">{t('platform:journal.explorer.detail.reversedBy')}</span>
                <span className="je-expanded-meta-value je-tbl-mono">{entry.reversedById}</span>
              </div>
            )}
            {entry.reversesId && (
              <div className="je-expanded-meta-item">
                <span className="je-expanded-meta-label">{t('platform:journal.explorer.detail.reverses')}</span>
                <span className="je-expanded-meta-value je-tbl-mono">{entry.reversesId}</span>
              </div>
            )}
            {entry.correlationId && (
              <div className="je-expanded-meta-item">
                <span className="je-expanded-meta-label">{t('platform:journal.explorer.detail.correlationId')}</span>
                <button
                  className="je-corr-btn"
                  onClick={() => onViewCorrelated(entry.correlationId!)}
                >
                  <Link2 size={12} />
                  {t('platform:journal.explorer.viewCorrelated')}
                </button>
              </div>
            )}
          </div>

          {/* Journal Lines sub-table */}
          <div className="je-lines-label">{t('platform:journal.explorer.detail.journalLines')}</div>
          {lines.length === 0 ? (
            <div className="je-center-state je-center-state-sm">
              <span>{t('platform:journal.explorer.detail.noLines')}</span>
            </div>
          ) : (
            <div className="je-tbl-wrap je-lines-tbl-wrap">
              <table className="je-tbl je-lines-tbl">
                <thead>
                  <tr>
                    <th>{t('platform:journal.explorer.lineColumns.accountCode')}</th>
                    <th>{t('platform:journal.explorer.lineColumns.accountName')}</th>
                    <th className="je-tbl-right">{t('platform:journal.explorer.lineColumns.debit')}</th>
                    <th className="je-tbl-right">{t('platform:journal.explorer.lineColumns.credit')}</th>
                    <th>{t('platform:journal.explorer.lineColumns.currency')}</th>
                    <th>{t('platform:journal.explorer.lineColumns.description')}</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <tr key={line.id}>
                      <td className="je-tbl-mono">{line.accountCode}</td>
                      <td>{line.accountName}</td>
                      <td className="je-tbl-right je-tbl-mono">
                        {line.debitAmount > 0
                          ? line.debitAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : '-'}
                      </td>
                      <td className="je-tbl-right je-tbl-mono">
                        {line.creditAmount > 0
                          ? line.creditAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : '-'}
                      </td>
                      <td>
                        <span className="je-currency-badge">{line.currency}</span>
                      </td>
                      <td className="je-tbl-muted">{line.description || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export function JournalExplorerPage() {
  const { t } = useTranslation('platform');

  // State
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [sourceTypeFilter, setSourceTypeFilter] = useState('');
  const [expandedEntryId, setExpandedEntryId] = useState<string | null>(null);
  const [correlationModal, setCorrelationModal] = useState<string | null>(null);

  // Data
  const { data: orgsData, isLoading: orgsLoading } = useOrganizations({ page: 0, size: 100 });
  const {
    data: entriesData,
    isLoading: entriesLoading,
    error: entriesError,
    refetch,
  } = useJournalEntries(
    selectedOrgId,
    currentPage,
    PAGE_SIZE,
    sourceTypeFilter || undefined
  );

  const organizations = orgsData?.content ?? [];
  const entries = entriesData?.content ?? [];
  const totalPages = entriesData?.totalPages ?? 0;
  const totalElements = entriesData?.totalElements ?? 0;
  const isFirst = entriesData?.first ?? true;
  const isLast = entriesData?.last ?? true;

  // Handlers
  const handleOrgChange = (orgId: string) => {
    setSelectedOrgId(orgId);
    setCurrentPage(0);
    setExpandedEntryId(null);
    setCorrelationModal(null);
  };

  const handleSourceTypeChange = (type: string) => {
    setSourceTypeFilter(type);
    setCurrentPage(0);
    setExpandedEntryId(null);
  };

  const handleRowClick = (entryId: string) => {
    setExpandedEntryId(expandedEntryId === entryId ? null : entryId);
  };

  const handleViewCorrelated = (correlationId: string) => {
    setCorrelationModal(correlationId);
  };

  return (
    <div className="je-page">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="je-header">
        <h1 className="je-title">
          <BookOpen className="je-title-icon" size={22} />
          {t('platform:journal.explorer.title')}
        </h1>
        {selectedOrgId && (
          <button className="je-btn" onClick={() => refetch()}>
            <RefreshCw size={14} /> {t('platform:journal.explorer.refresh')}
          </button>
        )}
      </div>

      {/* ── Filters ────────────────────────────────────────────────────── */}
      <div className="je-filters je-anim-1">
        <div className="je-filter-group">
          <label className="je-filter-label" htmlFor="je-org-select">
            {t('platform:journal.explorer.filters.organization')}
          </label>
          <div className="je-select-wrap">
            <select
              id="je-org-select"
              className="je-select"
              value={selectedOrgId}
              onChange={(e) => handleOrgChange(e.target.value)}
              disabled={orgsLoading}
            >
              <option value="">{t('platform:journal.explorer.filters.selectOrganization')}</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name} ({org.code})
                </option>
              ))}
            </select>
            <Search size={14} className="je-select-icon" />
          </div>
        </div>

        <div className="je-filter-group">
          <label className="je-filter-label" htmlFor="je-source-select">
            {t('platform:journal.explorer.filters.sourceType')}
          </label>
          <select
            id="je-source-select"
            className="je-select"
            value={sourceTypeFilter}
            onChange={(e) => handleSourceTypeChange(e.target.value)}
            disabled={!selectedOrgId}
          >
            <option value="">{t('platform:journal.explorer.filters.allSourceTypes')}</option>
            {SOURCE_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`platform:journal.explorer.sourceTypes.${type}`)}
              </option>
            ))}
          </select>
        </div>

        {selectedOrgId && !entriesLoading && (
          <div className="je-filter-summary">
            <span className="je-filter-summary-text">
              {t('platform:journal.explorer.totalEntries', { count: totalElements })}
            </span>
          </div>
        )}
      </div>

      {/* ── No Org Selected State ──────────────────────────────────────── */}
      {!selectedOrgId && (
        <div className="je-empty-state je-anim-2">
          <BookOpen size={40} className="je-empty-icon" />
          <h3 className="je-empty-title">{t('platform:journal.explorer.selectOrgPrompt')}</h3>
          <p className="je-empty-desc">{t('platform:journal.explorer.selectOrgDescription')}</p>
        </div>
      )}

      {/* ── Loading State ──────────────────────────────────────────────── */}
      {selectedOrgId && entriesLoading && (
        <div className="je-center-state je-anim-2">
          <RefreshCw className="je-spin" size={24} />
          <span>{t('platform:journal.explorer.loading')}</span>
        </div>
      )}

      {/* ── Error State ────────────────────────────────────────────────── */}
      {selectedOrgId && entriesError && (
        <div className="je-center-state je-err je-anim-2">
          <AlertTriangle size={24} />
          <span>{t('platform:journal.explorer.loadError')}</span>
        </div>
      )}

      {/* ── Entries Table ──────────────────────────────────────────────── */}
      {selectedOrgId && !entriesLoading && !entriesError && (
        <div className="je-section je-anim-2">
          <div className="je-tbl-wrap">
            <table className="je-tbl">
              <thead>
                <tr>
                  <th className="je-tbl-expand-col" />
                  <th>{t('platform:journal.explorer.columns.entryNumber')}</th>
                  <th>{t('platform:journal.explorer.columns.date')}</th>
                  <th>{t('platform:journal.explorer.columns.description')}</th>
                  <th>{t('platform:journal.explorer.columns.sourceType')}</th>
                  <th className="je-tbl-right">{t('platform:journal.explorer.columns.totalDebit')}</th>
                  <th className="je-tbl-right">{t('platform:journal.explorer.columns.totalCredit')}</th>
                  <th>{t('platform:journal.explorer.columns.status')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr>
                    <td colSpan={8} className="je-tbl-empty">
                      {t('platform:journal.explorer.noEntries')}
                    </td>
                  </tr>
                )}
                {entries.map((entry) => {
                  const srcStyle = getSourceTypeStyle(entry.sourceType);
                  const statusStyle = getStatusStyle(entry.status);
                  const isExpanded = expandedEntryId === entry.id;

                  return (
                    <>
                      <tr
                        key={entry.id}
                        className={`je-tbl-clickable ${isExpanded ? 'je-tbl-row-expanded' : ''}`}
                        onClick={() => handleRowClick(entry.id)}
                      >
                        <td className="je-tbl-expand-col">
                          {isExpanded
                            ? <ChevronDown size={14} className="je-expand-icon" />
                            : <ChevronRight size={14} className="je-expand-icon" />
                          }
                        </td>
                        <td className="je-tbl-mono je-tbl-entry-num">{entry.entryNumber}</td>
                        <td className="je-tbl-muted">
                          {new Date(entry.entryDate).toLocaleDateString()}
                        </td>
                        <td className="je-tbl-desc">{entry.description}</td>
                        <td>
                          <span
                            className="je-badge"
                            style={{ background: srcStyle.bg, color: srcStyle.color }}
                          >
                            {t(`platform:journal.explorer.sourceTypes.${entry.sourceType}`)}
                          </span>
                        </td>
                        <td className="je-tbl-right je-tbl-mono">
                          {entry.totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="je-tbl-right je-tbl-mono">
                          {entry.totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td>
                          <span
                            className="je-badge"
                            style={{ background: statusStyle.bg, color: statusStyle.color }}
                          >
                            {entry.status}
                          </span>
                        </td>
                      </tr>
                      {isExpanded && (
                        <ExpandedEntryDetail
                          key={`detail-${entry.id}`}
                          entryId={entry.id}
                          orgId={selectedOrgId}
                          onViewCorrelated={handleViewCorrelated}
                          t={t}
                        />
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ─────────────────────────────────────────────── */}
          {totalPages > 1 && (
            <div className="je-pag">
              <button
                className="je-btn"
                disabled={isFirst}
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft size={14} />
                {t('platform:journal.explorer.pagination.previous')}
              </button>
              <span className="je-pag-info">
                {t('platform:journal.explorer.pagination.pageInfo', {
                  current: currentPage + 1,
                  total: totalPages,
                })}
              </span>
              <button
                className="je-btn"
                disabled={isLast}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                {t('platform:journal.explorer.pagination.next')}
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Correlation Modal ──────────────────────────────────────────── */}
      {correlationModal && selectedOrgId && (
        <CorrelatedEntriesModal
          correlationId={correlationModal}
          orgId={selectedOrgId}
          onClose={() => setCorrelationModal(null)}
          t={t}
        />
      )}

      <style>{pageStyles}</style>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const pageStyles = `
  /* ── Page ──────────────────────────────────────────────────────────── */
  .je-page {
    display: flex; flex-direction: column; gap: 1.25rem;
    padding: 1.5rem 2rem; width: 100%; box-sizing: border-box;
  }

  /* ── Header ────────────────────────────────────────────────────────── */
  .je-header {
    display: flex; justify-content: space-between; align-items: center;
    animation: je-in-down 0.35s cubic-bezier(0.22,1,0.36,1) both;
  }
  .je-title {
    font: 700 1.4rem/1.2 var(--font-heading); color: var(--color-text-primary);
    display: flex; align-items: center; gap: 0.6rem; margin: 0;
  }
  .je-title-icon { color: var(--color-brand-primary); }

  .je-btn {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 7px 14px; font-size: 13px; font-weight: 500;
    border: 1px solid var(--color-border-default); border-radius: var(--radius-md);
    background: var(--color-bg-secondary); color: var(--color-text-primary);
    cursor: pointer; transition: all 0.15s;
  }
  .je-btn:hover:not(:disabled) { background: var(--color-bg-tertiary); border-color: var(--color-border-strong); }
  .je-btn:disabled { opacity: 0.45; cursor: not-allowed; }

  /* ── Filters ───────────────────────────────────────────────────────── */
  .je-filters {
    display: flex; align-items: flex-end; gap: 1rem; flex-wrap: wrap;
    padding: 1rem 1.15rem; border-radius: var(--radius-lg);
    background: var(--color-bg-secondary); border: 1px solid var(--color-border-subtle);
  }
  .je-filter-group {
    display: flex; flex-direction: column; gap: 0.35rem;
    min-width: 200px; flex: 1; max-width: 320px;
  }
  .je-filter-label {
    font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.04em; color: var(--color-text-muted);
  }
  .je-select-wrap { position: relative; }
  .je-select-icon {
    position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
    color: var(--color-text-muted); pointer-events: none;
  }
  .je-select {
    width: 100%; padding: 8px 12px; font-size: 13px;
    border: 1px solid var(--input-border); border-radius: var(--radius-md);
    background: var(--input-bg); color: var(--color-text-primary);
    cursor: pointer; appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    padding-right: 30px;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .je-select-wrap .je-select { padding-left: 30px; }
  .je-select:focus {
    outline: none; border-color: var(--color-brand-primary);
    box-shadow: 0 0 0 2px var(--input-focus-ring);
  }
  .je-select:disabled { opacity: 0.5; cursor: not-allowed; }

  .je-filter-summary {
    display: flex; align-items: center; padding-bottom: 2px;
    margin-left: auto;
  }
  .je-filter-summary-text {
    font-size: 12px; color: var(--color-text-muted); font-weight: 500;
  }

  /* ── Section ───────────────────────────────────────────────────────── */
  .je-section { }

  /* ── Staggered entrance ─────────────────────────────────────────────── */
  .je-anim-1 { animation: je-in-up 0.4s cubic-bezier(0.22,1,0.36,1) 0.04s both; }
  .je-anim-2 { animation: je-in-up 0.4s cubic-bezier(0.22,1,0.36,1) 0.08s both; }
  .je-anim-3 { animation: je-in-up 0.4s cubic-bezier(0.22,1,0.36,1) 0.12s both; }

  /* ── Table ─────────────────────────────────────────────────────────── */
  .je-tbl-wrap {
    background: var(--color-bg-secondary); border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-lg); overflow: hidden;
  }
  .je-tbl { width: 100%; border-collapse: collapse; }
  .je-tbl th {
    text-align: left; padding: 0.6rem 0.85rem; font-size: 10.5px; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.04em; color: var(--color-text-muted);
    background: var(--color-bg-tertiary); border-bottom: 1px solid var(--color-border-subtle);
    white-space: nowrap;
  }
  .je-tbl td {
    padding: 0.6rem 0.85rem; font-size: 13px; color: var(--color-text-primary);
    border-bottom: 1px solid var(--color-border-subtle);
  }
  .je-tbl tr:last-child td { border-bottom: none; }
  .je-tbl tbody tr:hover { background: var(--color-bg-tertiary); }
  .je-tbl-right { text-align: right !important; }
  .je-tbl-mono { font-family: monospace; font-size: 12px; }
  .je-tbl-muted { color: var(--color-text-muted); font-size: 12px; }
  .je-tbl-empty { text-align: center; color: var(--color-text-muted); padding: 2rem 1rem !important; }
  .je-tbl-desc {
    max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .je-tbl-entry-num { font-weight: 600; }

  /* Expand column */
  .je-tbl-expand-col { width: 32px; text-align: center !important; }
  .je-expand-icon { color: var(--color-text-muted); transition: color 0.15s; }

  /* Clickable rows */
  .je-tbl-clickable { cursor: pointer; transition: background 0.1s; }
  .je-tbl-clickable:hover .je-expand-icon { color: var(--color-brand-primary); }
  .je-tbl-row-expanded {
    background: var(--color-bg-tertiary) !important;
  }

  /* ── Badges ────────────────────────────────────────────────────────── */
  .je-badge {
    display: inline-block; padding: 2px 8px; border-radius: var(--radius-sm);
    font-size: 10.5px; font-weight: 700; letter-spacing: 0.02em;
    white-space: nowrap;
  }
  .je-currency-badge {
    display: inline-block; padding: 1px 6px; border-radius: var(--radius-sm);
    font-size: 10px; font-weight: 600; background: var(--color-bg-tertiary);
    color: var(--color-text-secondary);
  }

  /* ── Expanded Row ──────────────────────────────────────────────────── */
  .je-expanded-row td {
    padding: 0 !important; border-bottom: 1px solid var(--color-border-subtle) !important;
  }
  .je-expanded-row:hover { background: transparent !important; }
  .je-expanded-content {
    padding: 1rem 1.25rem 1.25rem 2.5rem;
    background: var(--color-bg-tertiary);
    border-top: 1px solid var(--color-border-subtle);
    animation: je-expand-in 0.25s cubic-bezier(0.22,1,0.36,1) both;
  }

  .je-expanded-meta {
    display: flex; flex-wrap: wrap; gap: 1rem 2rem; margin-bottom: 1rem;
  }
  .je-expanded-meta-item {
    display: flex; flex-direction: column; gap: 0.15rem;
  }
  .je-expanded-meta-label {
    font-size: 10px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.04em; color: var(--color-text-muted);
  }
  .je-expanded-meta-value {
    font-size: 13px; color: var(--color-text-primary); font-weight: 500;
  }

  .je-corr-btn {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 10px; font-size: 11.5px; font-weight: 600;
    border: 1px solid var(--color-brand-primary); border-radius: var(--radius-sm);
    background: transparent; color: var(--color-brand-primary);
    cursor: pointer; transition: all 0.15s;
  }
  .je-corr-btn:hover {
    background: var(--color-brand-primary); color: #fff;
  }

  .je-lines-label {
    font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.04em; color: var(--color-text-muted); margin-bottom: 0.5rem;
  }
  .je-lines-tbl-wrap {
    border-radius: var(--radius-md) !important;
  }
  .je-lines-tbl th {
    font-size: 10px; padding: 0.45rem 0.7rem;
  }
  .je-lines-tbl td {
    font-size: 12px; padding: 0.45rem 0.7rem;
  }

  /* ── Pagination ────────────────────────────────────────────────────── */
  .je-pag {
    display: flex; justify-content: center; align-items: center; gap: 0.75rem;
    margin-top: 0.75rem; padding: 0.5rem;
  }
  .je-pag-info { font-size: 12px; color: var(--color-text-muted); font-weight: 500; }

  /* ── Empty state ───────────────────────────────────────────────────── */
  .je-empty-state {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 0.75rem; padding: 4rem 2rem;
    background: var(--color-bg-secondary); border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-lg); text-align: center;
  }
  .je-empty-icon { color: var(--color-text-muted); opacity: 0.5; }
  .je-empty-title {
    font: 600 1rem var(--font-heading); color: var(--color-text-primary); margin: 0;
  }
  .je-empty-desc {
    font-size: 13px; color: var(--color-text-muted); margin: 0; max-width: 360px;
  }

  /* ── Center states ─────────────────────────────────────────────────── */
  .je-center-state {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 0.5rem; padding: 4rem; color: var(--color-text-secondary);
  }
  .je-center-state-sm { padding: 1.5rem; }
  .je-err { color: var(--color-error); }

  /* ── Modal ─────────────────────────────────────────────────────────── */
  .je-modal-overlay {
    position: fixed; inset: 0;
    background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(2px);
    display: flex; align-items: center; justify-content: center;
    z-index: 1000;
    animation: je-overlay-in 0.2s ease both;
  }
  .je-modal {
    background: var(--color-bg-secondary); border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-xl); box-shadow: var(--shadow-xl);
    width: 90%; max-width: 900px; max-height: 80vh;
    display: flex; flex-direction: column;
    animation: je-modal-in 0.3s cubic-bezier(0.22,1,0.36,1) both;
  }
  .je-modal-header {
    display: flex; align-items: center; gap: 0.5rem;
    padding: 1rem 1.25rem; border-bottom: 1px solid var(--color-border-subtle);
    flex-shrink: 0;
  }
  .je-modal-title-row {
    display: flex; align-items: center; gap: 0.4rem;
    color: var(--color-brand-primary);
  }
  .je-modal-title {
    font: 600 0.95rem var(--font-heading); color: var(--color-text-primary); margin: 0;
  }
  .je-modal-corr-id {
    font-family: monospace; font-size: 11px; color: var(--color-text-muted);
    background: var(--color-bg-tertiary); padding: 2px 8px; border-radius: var(--radius-sm);
    margin-left: 0.25rem;
  }
  .je-modal-close {
    margin-left: auto; padding: 6px; border: none; border-radius: var(--radius-sm);
    background: transparent; color: var(--color-text-muted); cursor: pointer;
    transition: all 0.15s;
  }
  .je-modal-close:hover { background: var(--color-bg-tertiary); color: var(--color-text-primary); }
  .je-modal-body {
    padding: 1rem 1.25rem; overflow-y: auto; flex: 1;
  }

  /* ── Animations ────────────────────────────────────────────────────── */
  @keyframes je-in-down {
    from { opacity: 0; transform: translateY(-8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes je-in-up {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes je-expand-in {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes je-overlay-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes je-modal-in {
    from { opacity: 0; transform: translateY(16px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes je-spin-kf {
    to { transform: rotate(360deg); }
  }
  .je-spin { animation: je-spin-kf 1s linear infinite; }

  @media (prefers-reduced-motion: reduce) {
    .je-header, .je-anim-1, .je-anim-2, .je-anim-3,
    .je-expanded-content, .je-modal-overlay, .je-modal {
      animation: none !important; transition: none !important;
    }
  }

  @media (max-width: 900px) {
    .je-tbl-desc { max-width: 160px; }
    .je-modal { width: 95%; max-width: 95%; }
  }

  @media (max-width: 640px) {
    .je-page { padding: 1rem; }
    .je-header { flex-direction: column; align-items: flex-start; gap: 0.75rem; }
    .je-filters { flex-direction: column; align-items: stretch; }
    .je-filter-group { max-width: none; }
    .je-filter-summary { margin-left: 0; }
    .je-tbl-desc { max-width: 120px; }
    .je-expanded-content { padding: 0.75rem; }
    .je-expanded-meta { gap: 0.75rem 1rem; }
    .je-modal { width: 98%; max-height: 90vh; }
  }
`;

export default JournalExplorerPage;
